// Content for the customer quote page modals (Session Z, Item 4D): the Terms and Conditions
// of Contract and the Maintenance Schedule. The T&C clauses are the LOCKED ESA wording from
// deliverables/contracts-v1.1/ELE_ESA_Standard_v2.html, ported faithfully (placeholders
// de-templated) in the brief's clause order. This is customer-facing on-screen copy, so it
// uses proper punctuation (no " -- ", no em dashes); the " -- " house style is for the PDF
// contracts only. The maintenance schedule text is the locked specification from the brief.

export type ClauseBlock = { kind: "p"; text: string } | { kind: "ul"; items: string[] };
export interface Clause {
  n: number;
  title: string;
  body: ClauseBlock[];
}

// Session AKG: this is now the CLIENT MIRROR of the server single source of truth
// (server/src/pdf/templates/maintenanceTerms.ts MAINTENANCE_TERMS_CLAUSES), pinned byte-equal by
// maintenanceTerms.parity.test.ts. It was the only home until AKG, which is why no customer email
// could attach the terms it named: nothing server-side could render them. The server source now
// feeds the Terms and Conditions PDF that travels with the welcome and the renewal confirmation;
// this mirror still feeds only the on-screen TermsModal. Do not edit one side without the other
// (the parity test goes red on drift), exactly as for MAINTENANCE_SCHEDULE below.
export const TERMS_CLAUSES: Clause[] = [
  {
    n: 1,
    title: "Purpose and Interpretation",
    body: [
      { kind: "p", text: "This Agreement sets out the terms under which the Contractor shall provide routine lift maintenance services in respect of the plant detailed in the Agreement (the Plant). This Agreement shall be governed by and construed in accordance with the Conditions of Contract set out below." },
    ],
  },
  {
    n: 2,
    title: "Commencement Date",
    body: [
      { kind: "p", text: "This Agreement commences on the preferred start date indicated, or on the date of the signed Agreement's return, whichever is later." },
    ],
  },
  {
    n: 3,
    title: "Term",
    body: [
      { kind: "p", text: "The Agreement shall remain in force for an initial period of twelve (12) months, subject to earlier termination in accordance with this Agreement, at the annual cost for the service tier selected by the Customer at quotation." },
    ],
  },
  {
    n: 4,
    title: "Payment Terms",
    body: [
      // Session AEV: was "monthly in advance", the same false statement corrected in the signed
      // ESA (esaContract.ts agreementPayment). This modal is the PRE-signature copy of the same
      // clause, so leaving it would have had the quotation the customer reads contradict the
      // contract they then sign. There is no billing-period value in scope here (TERMS_CLAUSES is
      // static copy), so the wording is frequency-neutral and points at the quotation itself,
      // which does state the servicing frequency (the same Contract.billingFrequency the ESA
      // derives its period from). NB the quotation shows ANNUAL figures plus a per-month
      // equivalent, never a per-billing-period amount: that appears first on the ESA.
      // Session AKH: "this quotation" is TRUE HERE and stays. The modal renders on the accept page
      // with the quotation itself on screen behind it, which is exactly what the comment above
      // reasoned. It is the STANDALONE Terms and Conditions PDF (server maintenanceTc.ts) that had
      // nothing for the phrase to point at, and only that context's wording changed: the server
      // source now builds the clauses per context (maintenanceTermsClauses) and this mirror is the
      // "quotation" one, still byte-equal to it. Do not "tidy" this into a contextless phrase to
      // match the PDF: that would throw away real information the reader can act on here.
      { kind: "p", text: "The Customer shall pay the Contractor in advance, at the billing frequency set out in this quotation. All prices are exclusive of VAT, which shall be added at the prevailing statutory rate. No discounts apply unless expressly agreed in writing." },
      { kind: "p", text: "Invoices unpaid after 30 days from the due date shall accrue interest at 8% per annum above the Bank of England base rate from the due date, pursuant to the Late Payment of Commercial Debts (Interest) Act 1998." },
    ],
  },
  {
    n: 5,
    title: "Price Review",
    body: [
      { kind: "p", text: "Prices are based on costs prevailing at the date of this Agreement and shall be adjusted annually in line with the Office for National Statistics Consumer Prices Index (CPI) plus 1.5%, subject to a floor of 0% and a cap of 10%. The contract price may also be varied to reflect changes in taxes, statutory obligations, or regulatory requirements." },
    ],
  },
  // Session AMQ: clause 6, the repair labour rate. Mirrors the server single source
  // (maintenanceTerms.ts), which carries the full reasoning: why the clause is self-contained
  // rather than cross-referring the way the ESA's 3.4 does, and why Liability through Anti-Bribery
  // each moved up one. Do not edit this side alone; maintenanceTerms.parity.test.ts goes red.
  {
    n: 6,
    title: "Repair Labour",
    body: [
      { kind: "p", text: "Where repair work to the Plant is charged by the hour rather than against a fixed quotation, repair labour is charged at £125.00 per hour for every hour on site, including the first, on weekdays before 18:00, and at £250.00 per hour on weekdays from 18:00, at weekends and on bank holidays. Those rates are the same whatever cover tier the Customer holds, and whether or not the Customer is in contract or this Agreement has expired." },
      { kind: "p", text: "There is no first hour premium on repair labour, and no cover tier discount applies to it: a first hour rate and any discount attaching to the Customer's cover tier are call-out charges, and apply to call-out attendance only. A call-out attendance lasts while the engineer stays on site, and everything done on it, including any repair, is charged at the call-out rates; a return visit after the engineer has left is a repair." },
    ],
  },
  {
    n: 7,
    title: "Liability",
    body: [
      { kind: "p", text: "The Contractor shall be liable for direct loss or damage to property and for personal injury or death arising from its negligence, breach of contract, or breach of statutory duty. The Contractor's total liability for property damage shall not exceed £5,000,000. The Contractor shall not be liable for:" },
      { kind: "ul", items: ["Loss of profit", "Loss of business", "Loss of contracts", "Any indirect or consequential loss"] },
      { kind: "p", text: "The Contractor shall not be liable for delay or failure caused by events beyond its reasonable control, including but not limited to fire, flood, industrial action, acts of government, or acts of God." },
      { kind: "p", text: "The Customer shall indemnify the Contractor against claims, losses, and costs arising from: inaccurate or incomplete instructions provided by the Customer; components or materials supplied by the Customer that prove defective; third-party interference with the plant not caused by the Contractor; or the Customer's failure to act on written recommendations made by the Contractor." },
    ],
  },
  {
    n: 8,
    title: "Termination",
    body: [
      { kind: "p", text: "Either party may terminate this Agreement by giving 90 days' written notice after the initial term. The Contractor may terminate immediately if:" },
      { kind: "ul", items: ["Payment is not made when due", "Access is repeatedly denied", "Third-party work is carried out without consent", "The Customer fails to carry out necessary works advised in writing", "The Plant is, in the Contractor's reasonable opinion, being used unsafely or unreasonably"] },
    ],
  },
  {
    n: 9,
    title: "Immobilisation",
    body: [
      { kind: "p", text: "Where the Contractor reasonably believes the Plant presents an imminent safety risk, it may temporarily immobilise the lift. The Customer shall be notified as soon as reasonably practicable. The Contractor shall not be liable for consequences arising from such action." },
    ],
  },
  {
    n: 10,
    title: "Access",
    body: [
      { kind: "p", text: "The Customer shall provide safe access and ensure compliance with the Health and Safety at Work etc. Act 1974 and associated regulations." },
    ],
  },
  {
    n: 11,
    title: "Insurance",
    body: [
      { kind: "p", text: "This Agreement does not constitute a contract of insurance. The Contractor shall maintain appropriate insurance in accordance with its legal obligations." },
    ],
  },
  {
    n: 12,
    title: "Governing Law",
    body: [
      { kind: "p", text: "This Agreement shall be governed by and construed in accordance with the laws of England and Wales." },
    ],
  },
  {
    n: 13,
    title: "Complaints",
    body: [
      { kind: "p", text: "The Contractor shall acknowledge complaints in writing within 5 working days of receipt. Where a defect is caused by the Contractor's negligence or breach of the agreed specification, the Contractor shall rectify it within: 7 working days for minor defects (cosmetic, non-urgent); 14 working days for functional defects (lift out of service); or as agreed in writing for complex defects requiring specialist parts or third-party involvement." },
      { kind: "p", text: "Rectification does not extend to: design changes or customer preference disputes; defects caused by third-party interference with the plant; defects arising from components not supplied by the Contractor; or works arising from insufficient maintenance prior to the Contractor's engagement. Specialist parts and third-party costs required for rectification shall be agreed in writing before being incurred." },
    ],
  },
  {
    n: 14,
    title: "Anti-Bribery",
    body: [
      { kind: "p", text: "The Contractor and its employees shall not engage in any activity that constitutes a breach of the Bribery Act 2010." },
    ],
  },
];

// Session APL (Director decision D60, 22 September 2026): the same clauses for an EQUIPMENT-ONLY quote (no
// lift, no plan-covered item, at least one care equipment item), whose page used to promise "routine lift
// maintenance services" at the cost of "the service tier selected". Four phrases change, each to the signed
// ESA's own wording for that shape. Built FROM TERMS_CLAUSES rather than as a second copy of all fourteen
// clauses, and pinned byte-equal to the server's maintenanceTermsClauses("quotation", "careEquipment") by
// maintenanceTerms.parity.test.ts: if the lift wording ever changes so a phrase below stops matching, the
// two sides disagree and that test goes red, which is the point.
const CARE_EQUIPMENT_TERMS_WORDING: [lift: string, careEquipment: string][] = [
  ["provide routine lift maintenance services", "provide routine care equipment maintenance services"],
  ["for the service tier selected", "for the care equipment maintenance service selected"],
  ["immobilise the lift.", "immobilise the equipment."],
  ["(lift out of service)", "(equipment out of service)"],
  // Session AQC (D72): clause 6 names a cover tier twice, on a shape that has none. The rate does not
  // move. The signed agreement's clause 3.4 drops the same two phrases for the same shape.
  [
    "bank holidays. Those rates are the same whatever cover tier the Customer holds, and whether or not the Customer is in contract or this Agreement has expired.",
    "bank holidays, whether or not the Customer is in contract or this Agreement has expired.",
  ],
  [
    "There is no first hour premium on repair labour, and no cover tier discount applies to it: a first hour rate and any discount attaching to the Customer's cover tier are call-out charges, and apply to call-out attendance only.",
    "There is no first hour premium on repair labour: a first hour rate is a call-out charge, and applies to call-out attendance only.",
  ],
];
function careEquipmentTermsText(text: string): string {
  return CARE_EQUIPMENT_TERMS_WORDING.reduce((t, [lift, care]) => t.split(lift).join(care), text);
}
export const TERMS_CLAUSES_CARE_EQUIPMENT: Clause[] = TERMS_CLAUSES.map((c) => ({
  ...c,
  body: c.body.map((b): ClauseBlock => (b.kind === "p" ? { kind: "p", text: careEquipmentTermsText(b.text) } : b)),
}));

// THE GATES-ONLY SET (Session AQC, Director decision D72): no lift, a tier-covered additional item (a
// gate, door, barrier or shutter). Its own signed agreement has named the plant "equipment" since
// Session ACV, while this page showed the lift terms. Only the three clauses that NAME the plant change;
// clause 3 keeps "the service tier selected", because a gates-only quote is tier-priced and genuinely has
// one, and clause 6 keeps its cover tier wording for the same reason. Built from TERMS_CLAUSES and pinned
// byte-equal to the server's maintenanceTermsClauses("quotation", "coveredItems") by the parity test.
const COVERED_ITEMS_TERMS_WORDING: [lift: string, equipment: string][] = [
  ["provide routine lift maintenance services", "provide routine equipment maintenance services"],
  ["immobilise the lift.", "immobilise the equipment."],
  ["(lift out of service)", "(equipment out of service)"],
];
export const TERMS_CLAUSES_COVERED_ITEMS: Clause[] = TERMS_CLAUSES.map((c) => ({
  ...c,
  body: c.body.map((b): ClauseBlock =>
    b.kind === "p" ? { kind: "p", text: COVERED_ITEMS_TERMS_WORDING.reduce((t, [lift, eq]) => t.split(lift).join(eq), b.text) } : b,
  ),
}));

export interface MaintenanceSchedule {
  included: string[];
  periodic: string[];
  notIncluded: string[];
  footer: string;
}

// Session AAN: this accept-page mirror is now pinned byte-equal to the server single source of
// truth (server/src/pdf/templates/serviceSpecification.ts, itself the verbatim signed-contract
// wording per ESA_CONTRACT_EXTRACT.md) by serviceSpecification.parity.test.ts. It previously
// carried a drifted copy ("/" -> "and", plus "per lift per year" / "by the duty holder"
// embellishments); realigned to the signed wording so the customer's on-screen schedule matches
// exactly what the ESA states and the quote PDF now shows. Do not edit one side without the other
// (the parity test goes red on drift). The "/" separators are the signed-contract wording.
export const MAINTENANCE_SCHEDULE: MaintenanceSchedule = {
  included: [
    "Full operational check of the lift (ride quality, levelling, door operation)",
    "Inspect all safety systems and critical components",
    "Check and adjust lift controls and signalling systems",
    "Inspect machine room / control equipment",
    "Inspect lift shaft equipment and running gear",
    "Test alarm system and emergency communication (EN 81-28)",
    "Lubricate all moving parts where required",
    "Clean key areas including door tracks and equipment zones",
    "Identify and report any faults, wear or potential issues",
  ],
  periodic: [
    "Landing and car doors, locks and safety edges",
    "Lift controls and safety circuits",
    "Shaft equipment and safety limits",
    "Suspension ropes / chains and fixings",
    "Drive systems (motor, gearbox, hydraulic unit)",
    "Buffers, guide systems and electrical wiring",
    "Emergency release systems",
  ],
  notIncluded: [
    "Major repairs or replacement of components. Where repair labour is charged by the hour rather than quoted, it is £125.00 per hour on weekdays before 18:00 and £250.00 per hour on weekdays from 18:00, at weekends and on bank holidays, the same at every cover tier and whether or not you hold a contract.",
    "Works arising from misuse, damage or vandalism",
    "Call-outs outside the included allowance (Core and Care tiers; Complete includes 6 out-of-hours call-outs per lift per year, with further call-outs at 25% off)",
    "Specialist parts, major components or upgrades",
    "LOLER thorough examinations (arranged separately)",
  ],
  footer: "All maintenance is carried out in accordance with LOLER 1998, PUWER 1998 and BS EN 81 standards.",
};

// Session APL (Director decision D60): the Maintenance Schedule for an EQUIPMENT-ONLY quote. The CLIENT
// MIRROR of the server's CARE_EQUIPMENT_SERVICE_SPEC and CARE_EQUIPMENT_SPEC_COMPLIANCE_NOTE
// (server/src/pdf/templates/serviceSpecification.ts, drawn from the ELE-MS-002 care equipment checklist),
// pinned byte-equal by serviceSpecification.parity.test.ts exactly as the lift schedule above is. periodic
// is empty on purpose (a care equipment visit runs the whole checklist), and the modal omits that heading.
export const MAINTENANCE_SCHEDULE_CARE_EQUIPMENT: MaintenanceSchedule = {
  included: [
    "Check each item's identification, RFID tag and data plate, including its safe working load",
    "Inspect the frame, track or chassis for damage, corrosion or misuse",
    "Check fixings are secure, castors turn freely and brakes hold",
    "Inspect straps, tapes and attachment points for fraying, cuts or damage",
    "Check hooks, spreader bars and safety catches work correctly",
    "Run every movement through its full range (lift, lower, traverse or profile), checking it is smooth, holds position and makes no unusual noise",
    "Test the limit switches (where fitted), the emergency stop and the emergency lowering or backup system",
    "Check the handset and controls, including any lockouts",
    "Check battery charge and charging, and that mains leads and plugs are undamaged",
    "Check bed side rails latch securely, without excessive play or unsafe gaps",
    "Check pads and upholstery for tears or damage",
    "Record any defects and parts needed, and tell the site contact before we leave",
    "Leave each item safe and in service, or take it out of service if it is not safe to use",
  ],
  periodic: [],
  notIncluded: [
    "Major repairs or replacement of components. Where repair labour is charged by the hour rather than quoted, it is £125.00 per hour on weekdays before 18:00 and £250.00 per hour on weekdays from 18:00, at weekends and on bank holidays, whether or not you hold a contract.",
    "Works arising from misuse, damage or vandalism",
    "Call-outs (chargeable at our Schedule of Fees rates)",
    "Specialist parts, major components or upgrades",
    "LOLER thorough examinations (arranged separately)",
  ],
  footer: "All maintenance is carried out in accordance with LOLER 1998, PUWER 1998 and the manufacturer's recommendations.",
};

// THE GATES-ONLY SCHEDULE (Session AQC, Director decision D72). A lift-less, tier-covered quote (a gate,
// door, barrier or shutter) keeps the LIFT specification, exactly as its own agreement does: the lists are
// this mirror's lift lists, untouched, so the two documents still match line for line. Only the closing
// standards sentence changes, because BS EN 81 is the lift standard and LOLER governs lifting equipment,
// neither of which a barrier is. Derived from the lift schedule rather than copied, so a change to the
// lists reaches both, and pinned against the server's lift spec plus COVERED_ITEMS_SPEC_COMPLIANCE_NOTE
// by serviceSpecification.parity.test.ts.
export const MAINTENANCE_SCHEDULE_COVERED_ITEMS: MaintenanceSchedule = {
  ...MAINTENANCE_SCHEDULE,
  footer: "All maintenance is carried out in accordance with PUWER 1998 and the manufacturer's recommendations.",
};
